/**
 * Config Registry Form Page Component
 * Create/Edit/View form for Config Registry items
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Alert,
  Tooltip,
  Button,
  Chip,
  IconButton,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CodeIcon from '@mui/icons-material/Code';
import DraftsIcon from '@mui/icons-material/Drafts';
import PublishIcon from '@mui/icons-material/Publish';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { useToast } from 'context/ToastContext';
import { BglLoading } from 'components/loading';
import {
  BglPrimarySmallButton,
  BglSecondarySmallButton,
} from 'components/button/BglButtons';
import {
  selectCurrentConfig,
  selectIsLoadingCurrentConfig,
  selectUISchema,
  selectConfigTypes,
  setCurrentConfig,
  setIsLoadingCurrent,
  setUISchema,
  setConfigTypes,
  updateCurrentConfigStatus,
} from '../store/configRegistryUISlice';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { ConfigRegistryFormData, ConfigRegistryItem, UISchemaResponse, CONFIG_STATUS_COLORS } from '../types/configRegistryTypes';
import { ConfigRegistryFormSections } from './ConfigRegistryFormSections';
import { WorkflowActionButtons } from './WorkflowActionButtons';
import { GenerateJsonModal } from './GenerateJsonModal';

export type FormMode = 'create' | 'edit' | 'view';

// Default form data for new configs
const getDefaultFormData = (): ConfigRegistryFormData => ({
  configKey: '',
  displayName: '',
  configDocumentation: '',
  configType: '',
  configDataType: 'TEXT',
  selectedLookups: [],
  templateItems: [{ value: '' }],
  possibleValues: null,
  validationRule: null,
  parentConfig: null,
  relatedConfigs: [],
  duplicateConfigs: [],
  dependsUpon: [],
  isMandatoryField: false,
  utilityConfigurationRequired: false,
  deprecated: false,
  specialHandlingRequired: false,
});

// Convert ConfigRegistryItem to FormData
const itemToFormData = (item: ConfigRegistryItem): ConfigRegistryFormData => ({
  configKey: item.configKey,
  displayName: item.displayName,
  configDocumentation: item.configDocumentation || '',
  configType: item.configType,
  configDataType: item.configDataType,
  // Extract lookup documentIds from _lookups array (when expand=full)
  selectedLookups: item._lookups?.map(l => l.documentId) || [],
  templateItems:
    (item.templateItems?.length ?? 0) > 0
      ? (item.templateItems ?? []).map((t) => ({
          value: t.value ?? ''
        }))
      : [{ value: '' }],
  possibleValues: item.possibleValues,
  validationRule: item.validationRule,
  parentConfig: item.parentConfig || null,
  relatedConfigs: item.relatedConfigs || [],
  duplicateConfigs: item.duplicateConfig || [],
  dependsUpon: item.configDependsUpon || [],
  isMandatoryField: item.isMandatoryField || false,
  utilityConfigurationRequired: item.utilityConfigurationRequired || false,
  deprecated: item.deprecated || false,
  specialHandlingRequired: item.specialHandlingRequired || false,
});

export const ConfigRegistryFormPage = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rc } = useGlobalConfig();
  const toast = useToast();
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  
  // Determine mode from URL
  const mode: FormMode = documentId 
    ? (searchParams.get('mode') === 'edit' ? 'edit' : 'view')
    : 'create';
  
  const currentConfig = useSelector(selectCurrentConfig);
  const isLoadingCurrent = useSelector(selectIsLoadingCurrentConfig);
  const uiSchema = useSelector(selectUISchema);
  const configTypes = useSelector(selectConfigTypes);

  const [formData, setFormData] = useState<ConfigRegistryFormData>(getDefaultFormData());
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [templateLoadWarning, setTemplateLoadWarning] = useState<string | undefined>();
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [createdConfig, setCreatedConfig] = useState<ConfigRegistryItem | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render after workflow actions

  // Use ref instead of window global for pending navigation (safer for multi-tab scenarios)
  const pendingNavigationRef = useRef<string | null>(null);

  // Analytics: fire high-frequency field-edit events only once per session.
  const fieldChangedTrackedRef = useRef(false);
  const nameEnteredTrackedRef = useRef(false);

  const canEdit = rc.permissionsManager.canConfigRegistryEdit();
  const isReadOnly = mode === 'view' || !canEdit;

  // Track editor open once on mount (create => EDITOR_OPENED for new config; edit/view => existing).
  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.EDITOR_OPENED, { mode })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Clear state when switching to create mode (fixes redundant data from previous edit)
  useEffect(() => {
    if (mode === 'create') {
      dispatch(setCurrentConfig(null));
      setFormData(getDefaultFormData());
      setError(undefined);
      setTemplateLoadWarning(undefined);
    }
  }, [mode, dispatch]);

  const fetchConfigWithFallback = useCallback(async (id: string): Promise<ConfigRegistryItem | null> => {
    setTemplateLoadWarning(undefined);
    const response = await ConfigRegistryApis.getConfigWithTemplate(rc, id);
    if (!response.error && response.data) {
      return (response.data as any).data || response.data;
    }

    const fallback = await ConfigRegistryApis.getConfig(rc, id, true);
    if (fallback.error || !fallback.data) {
      throw new Error(
        response.error?.message ||
          fallback.error?.message ||
          'Failed to load configuration details'
      );
    }

    const fallbackData = ((fallback.data as any).data || fallback.data) as ConfigRegistryItem;
    setTemplateLoadWarning(
      'Template values failed to load, showing metadata only. You can still edit metadata while template API is unavailable.'
    );
    return {
      ...fallbackData,
      templateItems: fallbackData.templateItems || []
    } as ConfigRegistryItem;
  }, [rc]);

  // Fetch config if editing/viewing - refetch when documentId changes
  useEffect(() => {
    if (documentId) {
      const fetchConfig = async () => {
        dispatch(setIsLoadingCurrent(true));
        try {
          setError(undefined);
          const configData = await fetchConfigWithFallback(documentId);
          if (configData) {
            dispatch(setCurrentConfig(configData));
          } else {
            setError('Failed to load config');
          }
        } catch (err: any) {
          setError(err.message || 'An error occurred');
        } finally {
          dispatch(setIsLoadingCurrent(false));
        }
      };
      fetchConfig();
    } else {
      // Clear config when no documentId (e.g., navigating to create mode)
      dispatch(setCurrentConfig(null));
      setTemplateLoadWarning(undefined);
    }
  }, [documentId, dispatch, fetchConfigWithFallback]);

  // Update form data when config loads (but not in create mode)
  useEffect(() => {
    if (currentConfig && mode !== 'create') {
      setFormData(itemToFormData(currentConfig));
      // Reset dirty flag after initial load to prevent false "unsaved changes"
      setIsDirty(false);
    }
  }, [currentConfig, mode]);

  // Fetch metadata if not cached
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        if (!uiSchema) {
          const schemaRes = await ConfigRegistryApis.getUISchema(rc);
          console.log('UI Schema Response:', schemaRes);
          if (schemaRes.data) {
            // Handle both lookupTypes and lookupFields structures
            // API returns { lookupFields: [...], totalTypes: N } or { lookupTypes: [...], hierarchy: {...} }
            const schemaData = schemaRes.data as any;
            const rawLookupTypes = schemaData.lookupTypes || schemaData.lookupFields || [];

            // Normalize the lookup types to have consistent field names
            const normalizedLookupTypes = rawLookupTypes.map((lt: any) => ({
              ...lt,
              // Ensure 'lookups' field exists (API might use 'options')
              lookups: (lt.lookups || lt.options || []).map((opt: any) => ({
                ...opt,
                // Normalize documentId (API might use 'value')
                documentId: opt.documentId || opt.value,
                displayName: opt.displayName || opt.label || opt.code,
              })),
            }));

            const normalizedSchema: UISchemaResponse = {
              lookupTypes: normalizedLookupTypes,
              hierarchy: schemaData.hierarchy || {},
            };
            console.log('Normalized UI Schema:', normalizedSchema);
            dispatch(setUISchema(normalizedSchema));
          }
        }
        if (configTypes.length === 0) {
          const typesRes = await ConfigRegistryApis.getConfigTypes(rc);
          console.log('Config Types Response:', typesRes);
          // Handle different response structures
          const typesData = typesRes.data as any;
          const types = typesData?.data || typesData || [];
          console.log('Extracted config types:', types);
          dispatch(setConfigTypes(Array.isArray(types) ? types : []));
        }
      } catch (err) {
        console.error('Failed to fetch metadata:', err);
      }
    };
    fetchMetadata();
  }, [rc, dispatch, uiSchema, configTypes.length]);

  // Refresh UI schema (force fetch)
  const handleRefreshUISchema = useCallback(async () => {
    try {
      const schemaRes = await ConfigRegistryApis.getUISchema(rc);
      if (schemaRes.data) {
        const schemaData = schemaRes.data as any;
        const rawLookupTypes = schemaData.lookupTypes || schemaData.lookupFields || [];
        const normalizedLookupTypes = rawLookupTypes.map((lt: any) => ({
          ...lt,
          lookups: (lt.lookups || lt.options || []).map((opt: any) => ({
            ...opt,
            documentId: opt.documentId || opt.value,
            displayName: opt.displayName || opt.label || opt.code,
          })),
        }));
        const normalizedSchema: UISchemaResponse = {
          lookupTypes: normalizedLookupTypes,
          hierarchy: schemaData.hierarchy || {},
        };
        dispatch(setUISchema(normalizedSchema));
      }
    } catch (err) {
      console.error('Failed to refresh UI schema:', err);
    }
  }, [rc, dispatch]);

  /**
   * Centralized refetch function - ensures UI is always in sync with server
   * Called after: save, submit, approve, reject, reopen
   */
  const refetchConfig = useCallback(async (): Promise<ConfigRegistryItem | null> => {
    if (!documentId) {
      return null;
    }

    try {
      const configData = await fetchConfigWithFallback(documentId);
      if (!configData) return null;

      // Use updateCurrentConfigStatus for workflow updates (also updates list)
      dispatch(updateCurrentConfigStatus(configData));

      // Force re-render by incrementing key FIRST
      setRefreshKey(prev => prev + 1);

      // Update form data AFTER key change
      setTimeout(() => {
        setFormData(itemToFormData(configData));
        setIsDirty(false);
      }, 0);

      return configData;
    } catch (err: any) {
      throw err;
    }
  }, [documentId, dispatch, fetchConfigWithFallback]);

  // Form field change handler
  const handleFieldChange = useCallback((field: keyof ConfigRegistryFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(undefined);
    setIsDirty(true);

    // Analytics: fire FIELD_CHANGED once per session to avoid per-keystroke noise.
    if (!fieldChangedTrackedRef.current) {
      fieldChangedTrackedRef.current = true;
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.FIELD_CHANGED);
    }

    // Create-mode specific field milestones.
    if (mode === 'create') {
      if (field === 'configKey' && value && !nameEnteredTrackedRef.current) {
        nameEnteredTrackedRef.current = true;
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.CONFIG_NAME_ENTERED);
      }
      if (field === 'configType' && value) {
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.CONFIG_TYPE_SELECTED, {
          config_type: value,
        });
      }
    }
  }, [rc, mode]);

  // Navigate back to list
  const handleBack = () => {
    dispatch(setCurrentConfig(null));
    navigate('/cms/config-registry');
  };

  // Validate form
  const validateForm = (): string | null => {
    if (!formData.configKey.trim()) return 'Config Key is required';
    if (!formData.displayName.trim()) return 'Display Name is required';
    if (!formData.configType.trim()) return 'Config Type is required';
    if (!formData.configDataType.trim()) return 'Config Data Type is required';
    if (!formData.configDocumentation.trim()) return 'Config Documentation is required';
    return null;
  };

  // Save handler
  const handleSave = async () => {
    // Validation is triggered on every save attempt.
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.VALIDATION_TRIGGERED);

    const validationError = validateForm();
    if (validationError) {
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.VALIDATION_FAILED, {
        error_type: 'schema_validation',
        severity: 'critical',
      });
      setError(validationError);
      return;
    }
    rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.VALIDATION_PASSED);

    setIsSaving(true);
    setError(undefined);

    try {
      const payload = {
        configKey: formData.configKey,
        displayName: formData.displayName,
        configDocumentation: formData.configDocumentation,
        configType: formData.configType,
        configDataType: formData.configDataType,
        possibleValues: formData.possibleValues || undefined,
        validationRule: formData.validationRule || undefined,
        lookups: formData.selectedLookups.length > 0 ? formData.selectedLookups : undefined,
        parentConfig: formData.parentConfig
          ? `${formData.parentConfig.configType}::${formData.parentConfig.configKey}`
          : undefined,
        relatedConfigs: formData.relatedConfigs.length > 0
          ? formData.relatedConfigs.map(c => `${c.configType}::${c.configKey}`)
          : undefined,
        duplicateConfig: formData.duplicateConfigs.length > 0
          ? formData.duplicateConfigs.map(c => `${c.configType}::${c.configKey}`)
          : undefined,
        configDependsUpon: formData.dependsUpon.length > 0
          ? formData.dependsUpon.map(c => `${c.configType}::${c.configKey}`)
          : undefined,
        isMandatoryField: formData.isMandatoryField,
        utilityConfigurationRequired: formData.utilityConfigurationRequired,
        deprecated: formData.deprecated,
        specialHandlingRequired: formData.specialHandlingRequired,
      };

      let response;
      if (mode === 'create') {
        response = await ConfigRegistryApis.createConfig(rc, payload);
      } else {
        response = await ConfigRegistryApis.updateConfig(rc, documentId!, payload);
      }

      if (response.error) {
        rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.PUBLISH_FAILED, {
          error_type: 'api_error',
          severity: 'critical',
        });
        setError(response.error.message || 'Failed to save config');
      } else {
        const savedDocId =
          mode === 'create'
            ? ((response.data as any)?.data || response.data)?.documentId
            : documentId;

        if (savedDocId && formData.templateItems.some((t) => t.value.trim())) {
          const templatePayload = formData.templateItems
            .filter((t) => t.value.trim())
            .map((t) => ({
              value: t.value
            }));
          if (templatePayload.length > 0) {
            const tplRes = await ConfigRegistryApis.saveConfigWithTemplate(
              rc,
              savedDocId,
              templatePayload
            );
            if (tplRes.error) {
              setError(tplRes.error.message || 'Metadata saved but template values failed');
              return;
            }
          }
        }

        if (mode === 'create' && response.data) {
          // Show success toast
          toast.success('Config created successfully!');

          // Get the created config data
          const createdData = (response.data as any).data || response.data;
          const newDocId = createdData.documentId;

          // Analytics: new config created successfully.
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.CONFIG_CREATED, {
            config_type: formData.configType,
          });

          // Store created config for JSON modal
          setCreatedConfig(createdData);

          // Show JSON modal
          setShowJsonModal(true);

          // Note: Navigation to edit page will happen when modal is closed
          // Store the documentId for later navigation using ref (safer than window global)
          pendingNavigationRef.current = newDocId;
        } else {
          toast.success('Config updated successfully!');

          // Analytics: edit-mode save persists the config as a draft.
          rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.DRAFT_SAVED, {
            config_type: formData.configType,
          });

          // Refetch to ensure UI is in sync with server
          try {
            await refetchConfig();
          } catch (err) {
            console.error('Failed to refetch after save:', err);
            // Still reset dirty flag even if refetch fails
            setIsDirty(false);
          }
        }
      }
    } catch (err: any) {
      rc.analytics.trackEvent(ANALYTICS_EVENTS.CONFIG_REGISTRY.PUBLISH_FAILED, {
        error_type: 'api_error',
        severity: 'critical',
      });
      setError(err.message || 'An error occurred while saving');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle JSON modal close - navigate to edit page
  const handleJsonModalClose = () => {
    setShowJsonModal(false);
    const pendingDocId = pendingNavigationRef.current;
    if (pendingDocId) {
      pendingNavigationRef.current = null;
      navigate(`/cms/config-registry/${pendingDocId}?mode=edit`);
    }
  };

  // Title based on mode
  const getTitle = () => {
    if (mode === 'create') return 'Create New Configuration';
    if (mode === 'edit') return `Edit: ${currentConfig?.displayName || 'Configuration'}`;
    return currentConfig?.displayName || 'View Configuration';
  };

  if (isLoadingCurrent) {
    return <BglLoading />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={handleBack}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <ArrowBackIcon fontSize="small" />
          Config Registry
        </Link>
        <Typography color="text.primary">
          {mode === 'create' ? 'New' : formData.configKey || 'Loading...'}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h5" fontWeight={600}>
            {getTitle()}
          </Typography>

          {/* Status Badge (visible in edit/view modes) */}
          {mode !== 'create' && currentConfig && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {/* Verification Status Badge */}
              {currentConfig.verificationStatus && (
                <Chip
                  label={currentConfig.verificationStatus.replace(/_/g, ' ')}
                  size="small"
                  sx={{
                    bgcolor: CONFIG_STATUS_COLORS[currentConfig.verificationStatus]?.bg || '#e5e7eb',
                    color: CONFIG_STATUS_COLORS[currentConfig.verificationStatus]?.color || '#374151',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              )}
            </Box>
          )}

          {/* Warning for configs created before workflow migration */}
          {mode !== 'create' && currentConfig && !currentConfig.verificationStatus && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip
                label="Pre-Migration Config"
                size="small"
                color="warning"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Generate JSON button (visible in edit/view modes) */}
          {mode !== 'create' && currentConfig && (
            <Tooltip
              title={isDirty ? "Please save your changes before generating JSON" : "Generate JSON for PR"}
              arrow
            >
              <span>
                <IconButton
                  onClick={() => setShowJsonModal(true)}
                  disabled={isDirty}
                  size="small"
                  sx={{
                    color: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.50',
                    },
                    '&.Mui-disabled': {
                      color: 'text.disabled',
                    },
                  }}
                >
                  <CodeIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {/* Create mode: Show Create button */}
          {mode === 'create' && (
            <>
              <BglSecondarySmallButton onClick={handleBack}>
                Back to List
              </BglSecondarySmallButton>
              <BglPrimarySmallButton
                onClick={handleSave}
                disabled={isSaving}
                startIcon={<SaveIcon />}
              >
                {isSaving ? 'Creating...' : 'Create Config'}
              </BglPrimarySmallButton>
            </>
          )}

          {/* Edit mode: Show Save icon button + Workflow actions */}
          {mode === 'edit' && !isReadOnly && (
            <>
              {isDirty && (
                <Tooltip title="Save changes" arrow>
                  <IconButton
                    onClick={handleSave}
                    disabled={isSaving}
                    color="primary"
                    size="small"
                    sx={{
                      bgcolor: 'primary.50',
                      '&:hover': { bgcolor: 'primary.100' },
                    }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          {/* View mode: Show Back button */}
          {mode === 'view' && (
            <BglSecondarySmallButton onClick={handleBack}>
              Back to List
            </BglSecondarySmallButton>
          )}

          {/* Workflow Action Buttons (visible in edit/view modes) */}
          {mode !== 'create' && currentConfig && (
            <WorkflowActionButtons
              key={`workflow-${refreshKey}-${currentConfig.verificationStatus}`}
              config={currentConfig}
              isDirty={isDirty}
              onSaveFirst={handleSave}
              onActionComplete={async (updatedConfig) => {
                // Use centralized refetch to ensure UI is in sync
                try {
                  const refreshedConfig = await refetchConfig();

                  // Use refreshed data for toast message (more accurate)
                  const finalConfig = refreshedConfig || updatedConfig;
                  const statusMessage = finalConfig.verificationStatus === 'APPROVED'
                    ? 'approved'
                    : finalConfig.verificationStatus === 'REJECTED'
                    ? 'rejected'
                    : finalConfig.verificationStatus === 'READY_FOR_QA'
                    ? 'submitted for review'
                    : 'updated';

                  toast.success(`Configuration ${statusMessage} successfully`);
                } catch (err) {
                  // Fallback: use the returned data and show toast anyway
                  dispatch(updateCurrentConfigStatus(updatedConfig));
                  setRefreshKey(prev => prev + 1);
                  setTimeout(() => {
                    setFormData(itemToFormData(updatedConfig));
                    setIsDirty(false);
                  }, 0);

                  const statusMessage = updatedConfig.verificationStatus === 'APPROVED'
                    ? 'approved'
                    : updatedConfig.verificationStatus === 'REJECTED'
                    ? 'rejected'
                    : updatedConfig.verificationStatus === 'READY_FOR_QA'
                    ? 'submitted for review'
                    : 'updated';
                  toast.success(`Configuration ${statusMessage} successfully`);
                }
              }}
              onError={(message) => {
                setError(message);
              }}
            />
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(undefined)}>
          {error}
        </Alert>
      )}

      {templateLoadWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setTemplateLoadWarning(undefined)}>
          {templateLoadWarning}
        </Alert>
      )}

      {/* Warning for configs without verificationStatus (created before migration) */}
      {mode !== 'create' && currentConfig && !currentConfig.verificationStatus && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This configuration was created before the workflow migration. Please save it to update to the new workflow system.
        </Alert>
      )}

      {/* Form Sections */}
      <ConfigRegistryFormSections
        key={`form-${refreshKey}-${currentConfig?.verificationStatus || 'create'}`}
        formData={formData}
        onFieldChange={handleFieldChange}
        uiSchema={uiSchema}
        configTypes={configTypes}
        isReadOnly={isReadOnly}
        mode={mode}
        currentConfig={currentConfig}
        onRefreshUISchema={handleRefreshUISchema}
        onLookupCreated={(typeCode, newLookup) => {
          console.log('Lookup created:', typeCode, newLookup);
        }}
      />

      {/* Generate JSON Modal */}
      {showJsonModal && (
        <GenerateJsonModal
          open={showJsonModal}
          onClose={handleJsonModalClose}
          config={createdConfig || currentConfig || formData}
          isFormData={!createdConfig && !currentConfig}
        />
      )}
    </Box>
  );
};
